import { useCallback, useRef, useState } from "react";

/**
 * Robust long-press hook for kiosk-lock unlock.
 *
 * Returns { progress, handlers } where:
 *  - `progress` is 0..1 — bind to a visual ring so the user knows
 *    their hold is being detected.
 *  - `handlers` is a prop bag you spread onto the target element.
 *
 * Why a custom hook (instead of a one-liner setTimeout):
 *  - On touch tablets, `pointercancel` fires for any tiny finger jitter.
 *    We absorb sub-30px movement to prevent spurious cancels.
 *  - We block `contextmenu` so the browser's text-selection / right-click
 *    menu doesn't interrupt the hold.
 *  - We tick progress so the UI can show a fill ring.
 */
export function useLongPress({ duration = 5000, onComplete, enabled = true }) {
  const [progress, setProgress] = useState(0);
  const startRef = useRef(0);
  const rafRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const completedRef = useRef(false);

  const stop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setProgress(0);
    completedRef.current = false;
  }, []);

  const tick = useCallback(() => {
    const elapsed = performance.now() - startRef.current;
    const p = Math.min(1, elapsed / duration);
    setProgress(p);
    if (p >= 1) {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
      stop();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [duration, onComplete, stop]);

  const onPointerDown = useCallback(
    (e) => {
      if (!enabled) return;
      // Only respond to primary button on mouse; touch/pen don't have a button.
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      startPosRef.current = { x: e.clientX, y: e.clientY };
      startRef.current = performance.now();
      completedRef.current = false;
      rafRef.current = requestAnimationFrame(tick);
    },
    [enabled, tick]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (rafRef.current == null) return;
      const dx = e.clientX - startPosRef.current.x;
      const dy = e.clientY - startPosRef.current.y;
      // Allow up to 30px of jitter — common on touch screens.
      if (Math.hypot(dx, dy) > 30) stop();
    },
    [stop]
  );

  const onPointerUp = useCallback(() => {
    stop();
  }, [stop]);

  const onPointerCancel = useCallback(() => {
    stop();
  }, [stop]);

  const onContextMenu = useCallback((e) => {
    // Prevent the OS context menu / text-selection callout on long touch.
    e.preventDefault();
  }, []);

  return {
    progress,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onContextMenu,
    },
  };
}
